import React, { useState, useEffect } from 'react';
import { expenseService } from '../services/expenseService';
import { FaPlus, FaTimes, FaEdit, FaTrash } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

function Expense() {
  const [showForm, setShowForm] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({
    description: 'Weekly Groceries',
    category: 'Food',
    date: new Date().toISOString().split('T')[0],
    amount: '75.50'
  });
  const [editingExpense, setEditingExpense] = useState(null);
  const [monthlyExpenses, setMonthlyExpenses] = useState([]);
  const [insights, setInsights] = useState({
    totalMonthly: 0,
    categoryTotals: {},
    averageExpense: 0,
    highestExpense: 0
  });

  const categories = [
    'Food',
    'Transportation',
    'Housing',
    'Utilities',
    'Entertainment',
    'Healthcare',
    'Shopping',
    'Education',
    'Travel',
    'Other'
  ];

  useEffect(() => {
    fetchExpenses();
  }, []);

  useEffect(() => {
    calculateInsights();
  }, [expenses]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('quickAdd') === 'true') {
      setShowForm(true);
    }
  }, []);

  const fetchExpenses = async () => {
    try { 
      const response = await expenseService.getExpenses();
      setExpenses(response.data);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await expenseService.createExpense(newExpense);
      setExpenses([...expenses, response.data]);
      setNewExpense({ description: '', category: '', date: new Date().toISOString().split('T')[0], amount: '' });
      setShowForm(false);
    } catch (error) {
      console.error('Error adding expense:', error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await expenseService.deleteExpense(id);
      setExpenses(expenses.filter(expense => expense._id !== id));
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleEdit = async (id, updatedExpense) => {
    if (!editingExpense) {
      // If not editing, set the expense to edit mode
      setEditingExpense(updatedExpense);
      return;
    }
    
    try {
      const response = await expenseService.updateExpense(id, editingExpense);
      if (response.ok) {
        const data = await response.json();
        setExpenses(expenses.map(expense => 
          expense._id === id ? data : expense
        ));
        setEditingExpense(null); // Clear editing state
      }
    } catch (error) {
      console.error('Error updating expense:', error);
    }
  };

  const calculateInsights = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const monthlyExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getMonth() === currentMonth && 
             expenseDate.getFullYear() === currentYear;
    });

    const categoryTotals = monthlyExpenses.reduce((acc, expense) => {
      acc[expense.category] = (acc[expense.category] || 0) + Number(expense.amount);
      return acc;
    }, {});

    const totalMonthly = monthlyExpenses.reduce((sum, expense) => sum + Number(expense.amount), 0);
    const averageExpense = totalMonthly / (monthlyExpenses.length || 1);
    const highestExpense = Math.max(...monthlyExpenses.map(expense => Number(expense.amount)), 0);

    setMonthlyExpenses(monthlyExpenses);
    setInsights({ totalMonthly, categoryTotals, averageExpense, highestExpense });
  };

  const totalExpenses = expenses.reduce((sum, expense) => sum + Number(expense.amount), 0);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const pieChartData = Object.entries(insights.categoryTotals).map(([name, value]) => ({
    name,
    value
  }));

  const barChartData = Object.entries(insights.categoryTotals).map(([name, value]) => ({
    name,
    amount: value
  }));

  // Add currency formatter
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-800">Expense Tracking</h1>
        <button 
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm"
        >
          {showForm ? <FaTimes className="text-sm" /> : <FaPlus className="text-sm" />}
          <span>{showForm ? 'Cancel' : 'Add New Expense'}</span>
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 mb-8 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Description"
              value={newExpense.description}
              onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <select
              value={newExpense.category}
              onChange={(e) => setNewExpense({...newExpense, category: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Select Category</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <input
              type="date"
              value={newExpense.date}
              onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <input
              type="number"
              placeholder="Amount"
              value={newExpense.amount}
              onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              step="0.01"
            />
          </div>
          <button type="submit" className="w-full md:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200">
            Add Expense
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Insights */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Monthly Insights</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm text-gray-600">Total Monthly</h3>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(insights.totalMonthly)}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="text-sm text-gray-600">Average Expense</h3>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(insights.averageExpense)}</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg">
                <h3 className="text-sm text-gray-600">Highest Expense</h3>
                <p className="text-2xl font-bold text-yellow-600">{formatCurrency(insights.highestExpense)}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="text-sm text-gray-600">Total Categories</h3>
                <p className="text-2xl font-bold text-purple-600">{Object.keys(insights.categoryTotals).length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Category Distribution</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Category Breakdown</h2>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="amount" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Right Side - Monthly Transactions */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <h2 className="text-xl font-semibold p-6 bg-gray-50">Monthly Transactions</h2>
          <div className="divide-y divide-gray-200">
            {expenses.map(expense => (
              <div key={expense._id} className="p-4 hover:bg-gray-50">
                {editingExpense && editingExpense._id === expense._id ? (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <input
                      type="date"
                      value={editingExpense.date}
                      onChange={e => setEditingExpense({...editingExpense, date: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="text"
                      value={editingExpense.description}
                      onChange={e => setEditingExpense({...editingExpense, description: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    />
                    <select
                      value={editingExpense.category}
                      onChange={e => setEditingExpense({...editingExpense, category: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                    <input
                      type="number"
                      value={editingExpense.amount}
                      onChange={e => setEditingExpense({...editingExpense, amount: e.target.value})}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                      step="0.01"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(expense._id, editingExpense)}
                        className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingExpense(null)}
                        className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div className="md:hidden font-semibold text-gray-600">Date:</div>
                    <div>{new Date(expense.date).toLocaleDateString()}</div>
                    
                    <div className="md:hidden font-semibold text-gray-600">Description:</div>
                    <div>{expense.description}</div>
                    
                    <div className="md:hidden font-semibold text-gray-600">Category:</div>
                    <div>{expense.category}</div>
                    
                    <div className="md:hidden font-semibold text-gray-600">Amount:</div>
                    <div className="font-semibold text-blue-600">{formatCurrency(expense.amount)}</div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(expense._id, expense)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(expense._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Expense;